/**
 * useThemeColors Hook
 * 
 * Manages theme color definitions (default, sepia, dracula)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getStorageItem, setStorageItem } from '../services/storage.service';
import { ThemeMode } from './useThemeMode';

export type ThemeName = 'default' | 'sepia' | 'dracula';

export interface ThemeColors {
  background: string;
  card: string;
  title: string;
  body: string;
  border: string;
  muted: string;
}

export interface UseThemeColorsReturn {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  themeColors: Record<ThemeName, Record<ThemeMode, ThemeColors>>;
  currentColors: ThemeColors;
}

export const useThemeColors = (mode: ThemeMode): UseThemeColorsReturn => {
  const [theme, setThemeState] = useState<ThemeName>('default');
  const [mounted, setMounted] = useState(false);

  // Theme color definitions
  const themeColors = useMemo<Record<ThemeName, Record<ThemeMode, ThemeColors>>>(() => ({
    default: {
      light: {
        background: '#ffffff',
        card: '#ffffff',
        title: '#000000',
        body: '#666666',
        border: '#d1d5db',
        muted: '#6b7280'
      },
      dark: {
        background: '#000000',
        card: '#000000',
        title: '#ffffff',
        body: '#cccccc',
        border: '#374151',
        muted: '#9ca3af'
      }
    },
    sepia: {
      light: {
        background: '#F5E6D3',
        card: '#F5E6D3',
        title: '#4A3728',
        body: '#7A5C42',
        border: '#D4C4B0',
        muted: '#9B8B7B'
      },
      dark: {
        background: '#2D1B0E',
        card: '#2D1B0E',
        title: '#F5E6D3',
        body: '#D4C4B0',
        border: '#5A4A3A',
        muted: '#9B8B7B'
      }
    },
    dracula: {
      light: {
        background: '#F8F8F2',
        card: '#F8F8F2',
        title: '#282A36',
        body: '#6272A4',
        border: '#BD93F9',
        muted: '#6272A4'
      },
      dark: {
        background: '#282A36',
        card: '#282A36',
        title: '#F8F8F2',
        body: '#BD93F9',
        border: '#6272A4',
        muted: '#6272A4'
      }
    }
  }), []);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = getStorageItem<ThemeName>('theme');
    if (savedTheme && ['default', 'sepia', 'dracula'].includes(savedTheme)) {
      setThemeState(savedTheme);
    }
    setMounted(true);
  }, []);

  // Persist theme changes and update CSS variables
  useEffect(() => {
    if (mounted) {
      setStorageItem('theme', theme);
      
      // Dispatch event for any listeners
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme, mode } }));
    }
  }, [mounted, theme, mode]);

  const setTheme = useCallback((newTheme: ThemeName) => {
    setThemeState(newTheme);
    
    // Dispatch event with the theme change so other hooks can react
    window.dispatchEvent(new CustomEvent('themeTypeChanged', { detail: { theme: newTheme } }));
  }, []);

  const currentColors = useMemo(() => {
    return themeColors[theme][mode];
  }, [themeColors, theme, mode]);

  return {
    theme,
    setTheme,
    themeColors,
    currentColors
  };
};

